import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Button from 'components/Button';
import Modal from 'components/Modal';
import TextArea from 'components/TextArea';
import SelectField from 'components/formFields/SelectField';
import { contactAgent } from 'services/BookingService';
import { getSavedGroupTags } from 'services/CommonService';
import { BUTTON_TYPE_SUBMIT } from 'shared/constants/common';
import style from '../../sass/style.module.scss';
import MessageModal from './MessageModal';

export default function ContactAgentModal({
  reference,
  openModal,
  setOpenModal,
  showReference,
  assignee = {},
  context = null,
}) {
  const CONTACT_OPTIONS = 'Self Service';
  const [message, setMessage] = useState(null);
  const [popupTitle, setPopupTitle] = useState('');
  const [popupMessage, setPopupMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [error, setError] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const { selectedBooking, selectedQuote } = useSelector(
    (state) => state.ui,
  );

  const resetForm = () => {
    setMessage(null);
    setSelectedContact(null);
  };

  useEffect(() => {
    if (!openModal) {
      resetForm();
    }
  }, [openModal]);

  const openModalHandler = () => {
    setOpenModal(!openModal);
  };

  const onChange = (e) => {
    setMessage(e.target.value);
  };

  const onChangeContactInfo = (e) => {
    setSelectedContact(e.target.value);
  };

  const submitHandler = async () => {
    setDisableSubmit(true);
    
    try {
      if (message !== null && selectedContact !== null) {
        const taskPayload = {
          message: `${message} \nContact: ${selectedContact}`,
        };

        if (assignee?.id) {
          taskPayload.assignee = { ...assignee };
        }

        if (context === 'booking') {
          await contactAgent({
            ...taskPayload,
            context,
            reference: reference || selectedBooking?.reference || null,
          });
        } else if (context === 'quote') {
          await contactAgent({
            ...taskPayload,
            context,
            reference: reference || selectedQuote?.reference || null,
          });
        } else {
          await contactAgent({ ...taskPayload });
        }

        setPopupTitle('Success');
        setPopupMessage('Sent Successfully');
        setShowMessage(true);
        setError(false);
        setDisableSubmit(false);

        openModalHandler();
      } else {
        setPopupTitle('Error');
        setPopupMessage('Please enter your message and your contact option');
        setShowMessage(true);
        setError(true);
      }
    } catch (e) {
      setPopupTitle('Error');
      setPopupMessage('Something Went Wrong...');
      setShowMessage(true);
      setError(true);

      console.error(e);
    }
  };

  const getContactAgentTags = useCallback(async () => {
    const defaultContactOptions = [
      {
        label: 'Email',
        value: 'Email',
      },
    ];

    let options = [];
    if (contactOptions.length <= 0) {
      const { data: contactOption } = await getSavedGroupTags([
        CONTACT_OPTIONS,
      ]);

      contactOption[CONTACT_OPTIONS].forEach((contactOpts) => {
        if (contactOpts.key === 'contactAgent') {
          if (contactOption[CONTACT_OPTIONS][0]) {
            contactOption[CONTACT_OPTIONS][0].values.forEach((option) => {
              options = [
                ...options,
                {
                  label: option,
                  value: option,
                },
              ];
            });
            setContactOptions(options);
          } else {
            setContactOptions(defaultContactOptions);
          }
        }
      });
    }
  }, [contactOptions]);

  useEffect(() => {
    if (contactOptions.length <= 0) {
      try {
        getContactAgentTags();
      } catch (e) {
        console.error(e);
      }
    }
  }, [contactOptions, getContactAgentTags]);

  return (
    <>
      {showMessage && (
        <MessageModal
          title={popupTitle}
          showModal={showMessage}
          message={popupMessage}
          openModalHandler={() => setShowMessage(false)}
          error={error}
        />
      )}
      <Modal
        show={openModal}
        onClose={openModalHandler}
        header="Message Customer Services"
        body={
          <>
            {showReference && (
              <div
                className={`text-xl leading-normal text-gray-800 ${style.modalHeader} ${style.locationHeader} p-4 mb-5`}
              >
                <h4>{reference}</h4>
              </div>
            )}

            <div
              className={`text-xl font-medium leading-normal text-gray-800 py-3 ${style.modalHeader}`}
            >
              Your Message
            </div>
            <div className="flex flex-wrap">
              <div className="w-full">
                <TextArea
                  label="Type your message here"
                  name="title"
                  value={message}
                  onChange={onChange}
                />
              </div>
            </div>
            <div className="select-field">
              <SelectField
                name="contact"
                label="How would you like to be contacted?"
                options={contactOptions}
                value={selectedContact}
                onChange={onChangeContactInfo}
              />
            </div>
          </>
        }
        footer={
          <Button
            type={BUTTON_TYPE_SUBMIT}
            label="Send"
            className="px-12"
            onClick={submitHandler}
            disabled={disableSubmit}
          />
        }
        justifyButtons="justify-end"
      />
    </>
  );
}
